/* =========================================================
   GC 2026 TOP PICKS SECTION
   File: assets/gc-2026-toppicks-section.js
========================================================= */

(() => {
  const getShopRoot = () => {
    if (window.Shopify?.routes?.root) return window.Shopify.routes.root;
    return '/';
  };

  const setButtonState = (button, loading, loadingText = 'Loading...') => {
    if (!button) return;

    if (!button.dataset.originalHtml) {
      button.dataset.originalHtml = button.innerHTML;
    }

    button.classList.toggle('is-loading', loading);
    button.disabled = loading;

    if (loading) {
      button.textContent = loadingText;
    } else {
      button.innerHTML = button.dataset.originalHtml;
    }
  };

  const updateCartCounts = async () => {
    try {
      const response = await fetch(`${getShopRoot()}cart.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;

      const cart = await response.json();

      document
        .querySelectorAll(
          '.gc-2026-header__cart-count, .cart-count-bubble span[aria-hidden="true"]'
        )
        .forEach((count) => {
          count.textContent = cart.item_count;
          count.classList.toggle('is-empty', cart.item_count === 0);
        });

      document.dispatchEvent(
        new CustomEvent('gc:cart-updated', { detail: { cart } })
      );
    } catch (error) {
      console.warn('GC Top Picks: cart count could not be refreshed.', error);
    }
  };

  const addProduct = async (form) => {
    const response = await fetch(`${getShopRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new FormData(form)
    });

    const payload = await response.json();

    if (!response.ok) {
      throw new Error(
        payload.description ||
          payload.message ||
          'This product could not be added.'
      );
    }

    return payload;
  };

  const initForms = (section) => {
    section
      .querySelectorAll('.gc-2026-toppicks-card__form')
      .forEach((form) => {
        if (form.dataset.gcToppicksFormReady === 'true') return;
        form.dataset.gcToppicksFormReady = 'true';

        const addButton = form.querySelector('[data-gc-toppicks-add]');
        const buyButton = form.querySelector('[data-gc-toppicks-buy]');
        const status = form.querySelector('[data-gc-toppicks-status]');

        if (addButton && !addButton.dataset.originalHtml) addButton.dataset.originalHtml = addButton.innerHTML;
        if (buyButton && !buyButton.dataset.originalHtml) buyButton.dataset.originalHtml = buyButton.innerHTML;

        form.addEventListener('submit', async (event) => {
          event.preventDefault();

          if (!addButton || addButton.disabled) return;

          setButtonState(addButton, true, 'Adding...');
          if (status) status.textContent = '';

          try {
            await addProduct(form);
            await updateCartCounts();

            addButton.innerHTML = 'Added to cart';
            addButton.disabled = false;
            addButton.classList.remove('is-loading');

            if (status) status.textContent = 'Added to cart';

            window.setTimeout(() => {
              addButton.innerHTML = addButton.dataset.originalHtml;
              if (status) status.textContent = '';
            }, 1800);
          } catch (error) {
            setButtonState(addButton, false);
            if (status) status.textContent = error.message;
          }
        });

        buyButton?.addEventListener('click', async () => {
          if (buyButton.disabled) return;

          setButtonState(buyButton, true, 'Loading...');
          if (status) status.textContent = '';

          try {
            await addProduct(form);
            window.location.assign(`${getShopRoot()}checkout`);
          } catch (error) {
            setButtonState(buyButton, false);
            if (status) status.textContent = error.message;
          }
        });
      });
  };

  const initCarousel = (section) => {
    const viewport = section.querySelector('[data-gc-toppicks-viewport]');
    const previous = section.querySelector('[data-gc-toppicks-prev]');
    const next = section.querySelector('[data-gc-toppicks-next]');

    if (!viewport) return;

    const updateArrows = () => {
      if (!previous || !next) return;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const hasOverflow = maxScroll > 4;
      previous.hidden = !hasOverflow;
      next.hidden = !hasOverflow;
      previous.disabled = viewport.scrollLeft <= 4;
      next.disabled = viewport.scrollLeft >= maxScroll - 4;
    };

    const getScrollAmount = () => {
      const firstCard = viewport.querySelector('.gc-2026-toppicks-card');
      const track = viewport.querySelector('.gc-2026-toppicks-section__track');
      if (!firstCard || !track) return viewport.clientWidth * 0.85;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return firstCard.getBoundingClientRect().width + gap;
    };

    previous?.addEventListener('click', () => {
      viewport.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      viewport.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
    });

    viewport.addEventListener('scroll', updateArrows, { passive: true });
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        viewport.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        viewport.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      }
    });

    window.addEventListener('resize', updateArrows);
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateArrows);
      observer.observe(viewport);
    }
    window.requestAnimationFrame(updateArrows);
  };

  const initTopPicks = (scope = document) => {
    scope.querySelectorAll('[data-gc-2026-toppicks]').forEach((section) => {
      if (section.dataset.gcToppicksReady === 'true') return;
      section.dataset.gcToppicksReady = 'true';
      initCarousel(section);
      initForms(section);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initTopPicks());
  document.addEventListener('shopify:section:load', (event) => initTopPicks(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest('.gc-2026-toppicks-card');
    const viewport = event.target.closest('[data-gc-toppicks-viewport]');
    if (!card || !viewport) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  });
})();